// React
import { useState, useMemo } from "react";
// Third-party
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Palette,
  Paper,
  Stack,
  Tooltip,
} from "@mui/material";
import { Bookmark, Copy } from "lucide-react";

// Internal
import useCopyToClipboard from "@hooks/useCopyToClipboard";
import { useChatHistory } from "@pages/askDost/hooks/useChatHistory";
import { MessageMarkdown } from "../blocks/MessageMarkdown";
import { ThinkingBlock } from "../blocks/ThinkingBlock";
import { copyTextToClipboard } from "@pages/askDost/utils/utils";
import { renderBlock } from "../blocks";
import { ReferencedDocsChips } from "../document/ReferencedDocsChips";
import { AttachmentsList } from "@pages/askDost/utils/AttachmentList";
import { AIMessageActions } from "../actions/AIMessageActions";
import {
  chatMessageRowSx,
  chatMessageColumnSx,
  userMessageHoverWrapperSx,
  userMessagePaperSx,
  userMessageCopyRowSx,
  showMoreButtonSx,
  aiPlainTextPaperSx,
} from "../../styles/ChatMessageStyle";

// Types
import { IChatBlock, IChatMessage } from "@pages/askDost/types";
import { USER_MESSAGE_COLLAPSE_LENGTH } from "@pages/askDost/utils/constants";

interface ChatMessageProps {
  message: IChatMessage;
  palette: Palette;
  translate: (text: string, options?: any) => string;
  onRegenerate?: (messageId: string) => void;
  onEditInstructions?: () => void;
  isRegeneration?: boolean;
  onSendMessage?: (message: string) => void;
  isStreaming?: boolean;
}

const getPendingThinkingBlock = (
  messageId: string | undefined,
  translate: (text: string, options?: any) => string,
): IChatBlock => ({
  id: `${messageId}-pending-thinking`,
  type: "thinking",
  thinkingSteps: [
    {
      label: translate("understanding_your_query"),
      done: false,
    },
  ],
});

const CollapsibleUserMessage = ({
  content,
  palette,
  translate,
}: {
  content: string;
  palette: Palette;
  translate: (text: string, options?: any) => string;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isLong = (content?.length ?? 0) > USER_MESSAGE_COLLAPSE_LENGTH;

  if (!isLong) {
    return <MessageMarkdown content={content} palette={palette} />;
  }

  return (
    <>
      <Collapse in={isExpanded} collapsedSize={"4em"}>
        <MessageMarkdown content={content} palette={palette} />
      </Collapse>
      <Button
        size="small"
        onClick={() => setIsExpanded(!isExpanded)}
        sx={showMoreButtonSx}
      >
        {translate(isExpanded ? "show_less" : "show_more")}
      </Button>
    </>
  );
};

const groupMessageBlocks = (blocks: IChatBlock[]) => {
  if (!blocks || blocks?.length === 0) return [];

  let mergedThinking: any = null;

  const remaining = blocks.filter((block) => {
    const blockType = block?.type || (block as any)?.t;
    if (blockType !== "thinking" && blockType !== "tool") return true;

    if (!mergedThinking) {
      mergedThinking = {
        ...block,
        id: block?.id || "merged-thinking",
        type: "thinking",
        groupedTools: blockType === "tool" ? [block] : [],
      };
      if (blockType === "tool") delete mergedThinking.content;
      return false;
    }

    if (blockType === "thinking" && block?.content) {
      mergedThinking.content = mergedThinking.content
        ? mergedThinking.content + "\n\n" + block.content
        : block.content;
    }

    if (blockType === "tool") {
      mergedThinking.groupedTools.push(block);
    }

    return false;
  });

  const grouped: any[] = [];

  remaining.forEach((block) => {
    const blockType = block?.type || (block as any)?.t;
    const isArtifact = blockType === "artifact";
    const innerBlock = isArtifact ? (block as any)?.a : block;
    const actualType = isArtifact ? innerBlock?.t : blockType;
    const toolName = block?.confirmTool || innerBlock?.tool;

    const lastGrouped = grouped[grouped.length - 1];

    if (lastGrouped) {
      const lastType = lastGrouped?.type || lastGrouped?.t;
      const lastIsArtifact = lastType === "artifact";
      const lastInner = lastIsArtifact ? lastGrouped?.a : lastGrouped;
      const lastActualType = lastIsArtifact ? lastInner?.t : lastType;
      const lastToolName = lastGrouped?.confirmTool || lastInner?.tool;

      const isGroupable =
        (actualType === "write" || actualType === "confirm_request") &&
        (lastActualType === "write" || lastActualType === "confirm_request") &&
        toolName === lastToolName &&
        toolName !== undefined;

      if (isGroupable) {
        if (!lastGrouped.mappingRuleEntries) {
          lastGrouped.mappingRuleEntries = [
            {
              write_id: lastInner?.write_id || lastInner?.id,
              rules: lastInner?.rules || {},
              state: lastInner?.state,
              note: lastInner?.note,
            },
          ];
        }

        lastGrouped.mappingRuleEntries.push({
          write_id: innerBlock?.write_id || innerBlock?.id,
          rules: innerBlock?.rules || {},
          state: innerBlock?.state,
          note: innerBlock?.note,
        });
        return;
      }
    }
    grouped.push({ ...block });
  });

  return mergedThinking ? [mergedThinking, ...grouped] : grouped;
};

export const ChatMessage = ({
  message,
  palette,
  translate,
  onRegenerate,
  onEditInstructions,
  isRegeneration,
  onSendMessage,
  isStreaming,
}: ChatMessageProps) => {
  const isUser = message?.sender === "user";

  const copiedText = copyTextToClipboard(message);
  const { copied, handleCopy } = useCopyToClipboard();
  const { savedPrompts, toggleSavedPrompt } = useChatHistory({ translate });
  const isPromptSaved =
    isUser && savedPrompts?.some((p) => p?.text === message?.content);

  const groupedBlocks = useMemo(
    () => groupMessageBlocks(message?.blocks || []),
    [message?.blocks],
  );

  const hasThinkingBlock = groupedBlocks?.some((block) => {
    const type = block?.type || (block as any)?.t;
    return type === "thinking" || type === "tool";
  });

  const renderUserMessage = () => (
    <Box sx={userMessageHoverWrapperSx}>
      <Paper sx={userMessagePaperSx(palette)}>
        {message?.attachments && message?.attachments?.length > 0 ? (
          <Box sx={{ ml: "auto", mb: 1 }}>
            <AttachmentsList
              attachments={message.attachments}
              palette={palette}
            />
          </Box>
        ) : null}
        <CollapsibleUserMessage
          content={message?.content}
          palette={palette}
          translate={translate}
        />
      </Paper>
      <Stack
        direction="row"
        className="user-message-copy"
        sx={userMessageCopyRowSx}
      >
        <Tooltip title={copied ? translate("copied") : translate("copy")}>
          <IconButton size="small" onClick={() => handleCopy(message?.content)}>
            <Copy size={18} />
          </IconButton>
        </Tooltip>
        <Tooltip
          title={translate(
            isPromptSaved ? "saved_to_prompts" : "save_prompt",
          )}
        >
          <IconButton
            size="small"
            onClick={() => toggleSavedPrompt(message?.content)}
          >
            <Bookmark
              size={18}
              fill={isPromptSaved ? palette.secondary.dark : "none"}
              color={isPromptSaved ? palette.secondary.dark : undefined}
            />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );

  const renderPendingThinking = () => (
    <ThinkingBlock
      block={getPendingThinkingBlock(message?.id, translate)}
      palette={palette}
      translate={translate}
      isStreaming
    />
  );

  const renderGroupedBlocks = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {isStreaming && !hasThinkingBlock && renderPendingThinking()}
      {groupedBlocks.map((block, index) => (
        <Box key={block?.id || index}>
          {renderBlock(
            block,
            palette,
            translate,
            onEditInstructions,
            isStreaming,
            onSendMessage,
          )}
          {block?.referencedDocs && block?.referencedDocs?.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <ReferencedDocsChips
                docs={block.referencedDocs}
                palette={palette}
                translate={translate}
              />
            </Box>
          )}
        </Box>
      ))}
      <AIMessageActions
        messageId={message?.id}
        content={copiedText}
        translate={translate}
        onRegenerate={onRegenerate}
        isRegeneration={isRegeneration}
      />
    </Box>
  );

  const renderPlainTextMessage = () => (
    <>
      <Paper sx={aiPlainTextPaperSx(palette)}>
        <MessageMarkdown content={message?.content} palette={palette} />
      </Paper>
      <AIMessageActions
        messageId={message?.id}
        content={copiedText}
        translate={translate}
        onRegenerate={onRegenerate}
        isRegeneration={isRegeneration}
      />
    </>
  );

  const renderAiMessage = () => {
    if (groupedBlocks && groupedBlocks?.length > 0) return renderGroupedBlocks();
    if (message?.content) return renderPlainTextMessage();
    if (isStreaming || isRegeneration) return renderPendingThinking();
    return null;
  };

  return (
    <Stack direction="row" sx={chatMessageRowSx(isUser)}>
      <Box sx={chatMessageColumnSx(isUser)}>
        {isUser ? renderUserMessage() : renderAiMessage()}
      </Box>
    </Stack>
  );
};
